import { Button } from "@nextui-org/react";
import { faClose } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IApiError } from "@/lib/types";

// ---------------------- Props

interface ErrorDisplayProps {
  error: IApiError;
  onClose?: () => void;
}

// ---------------------- Component

const ErrorDisplay = ({ error, onClose }: ErrorDisplayProps) => {
  // --------- JSX

  return (
    <div className="flex h-dvh w-screen flex-col items-center justify-center overflow-hidden bg-gray-50 p-6 dark:bg-gray-950">
      <main className="standard-shadow h-auto w-full max-w-[600px] flex-col overflow-hidden rounded-xl bg-white p-1 dark:bg-gray-900">
        <div className="size-full overflow-y-auto overflow-x-hidden rounded-xl border border-gray-50 p-3 dark:border-gray-950 xs:p-4 sm:p-6 md:p-8">
          <div className="flex h-auto w-full flex-col items-start justify-center gap-1">
            <div className="flex h-auto w-full flex-row items-start justify-between">
              {/*******************************************/}
              {/* Header */}
              {/*******************************************/}

              <div className="flex h-full w-auto flex-col items-start justify-start">
                <h1 className="mb-4 text-xl font-semibold text-gray-800 dark:text-gray-200 xs:text-2xl sm:text-3xl">
                  Something went wrong.
                </h1>
                <p className="mb-2 text-lg font-medium text-gray-800 dark:text-gray-400 xs:text-base">
                  Please try again in a moment.
                </p>
                <p className="text-sm font-normal text-gray-800 dark:text-gray-200 xs:text-base">
                  If this issue persists, please contact support and provide them with the details
                  shown below (or a screenshot of this error).
                </p>
              </div>

              {/*******************************************/}
              {/* Close Button */}
              {/*******************************************/}

              {onClose && (
                <Button
                  isIconOnly
                  radius="full"
                  className="bg-transparent p-2 text-current transition-all duration-200"
                  onPress={onClose}
                >
                  <FontAwesomeIcon
                    icon={faClose}
                    className="-mr-4 -mt-1 h-4 text-gray-800 transition-transform duration-500 dark:text-gray-200 xs:h-5"
                  />
                </Button>
              )}
            </div>

            {/*******************************************/}
            {/* Problem Details */}
            {/*******************************************/}

            {error.problemDetails && (
              <div className="mt-6 h-auto w-full rounded-lg bg-gray-950/50 p-4">
                <h2 className="mb-4 text-sm text-gray-400 underline">Problem Details</h2>
                {error.problemDetails.title && (
                  <div className="bg-red-950/50 rounded-lg">
                    <h3 className="text-red-200 text-base font-medium">
                      {error.problemDetails.title}
                    </h3>

                    {error.problemDetails.detail && (
                      <p className="mt-1 text-sm text-gray-300">
                        Details: {error.problemDetails.detail}
                      </p>
                    )}

                    {error.problemDetails.status && (
                      <p className="mt-1 text-sm text-gray-300">
                        Code: {error.problemDetails.status}
                      </p>
                    )}

                    {error.problemDetails.timestamp && (
                      <p className="mt-1 text-sm text-gray-300">
                        Timestamp: {error.problemDetails.timestamp}
                      </p>
                    )}

                    {error.problemDetails.traceId && (
                      <p className="mt-1 text-sm text-gray-300">
                        Trace ID: {error.problemDetails.traceId}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ErrorDisplay;
